const { useEffect, useRef, useState } = React;
import { Api } from "../../platform/api.js";
import { bgUrl, dbGetBoard, dbGetWeekdayDefault, dbSetLive, dbSubscribeLive } from "../data.jsx";
import { useT } from "../i18n.jsx";
import { emptyBoard, fromKey, getBlocksFromDay, stageCueIds, stageFightStart, weekdayOf, withBoardDefaults } from "../model.jsx";
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Icon, PanelBottom, PanelRight, Split, colorOf } from "../theme.jsx";

// ================================================================ LIVE MODE =====

function cueAtIndex(blocks, i) { return blocks.find((b) => b.start <= i && i < b.start + b.span) || null; }
function nextCueAfter(blocks, i) { const up = blocks.filter((b) => b.start > i).sort((a, b) => a.start - b.start); return up[0] || null; }

// Live Mode seats a producer assigns from the team list. Only the main seat
// advances cues (everything moves together); ON-GROUND / ON-AIR seats mark
// who runs that side, and their screens open focused on it.
const SEATS = [
  { key: "controller", idF: "controllerId", nameF: "controllerName", labelKey: "seatMain" },
  { key: "onGround", idF: "onGroundId", nameF: "onGroundName", labelKey: "seatOnGround" },
  { key: "onAir", idF: "onAirId", nameF: "onAirName", labelKey: "seatOnAir" },
];
const seatOfColumn = (colKey) => (colKey === "master" ? SEATS[0] : colKey === "onGround" ? SEATS[1] : SEATS[2]);

function LiveModeScreen({ date, perms, onExit, viewer = {} }) {
  const { t } = useT();
  const [board, setBoard] = useState(null);
  const [session, setSession] = useState({ status: "idle", currentIndex: 0 });
  const [now, setNow] = useState(new Date());
  const [layout, setLayout] = useState("side"); // side | bottom | grid
  const [pin, setPin] = useState("master");
  const [gridCols, setGridCols] = useState(null); // null = "all columns" (default); else array of keys
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [dir, setDir] = useState(1);
  const touchStartX = useRef(null);
  const prevIdxRef = useRef(0);
  const startedRef = useRef(false);
  // canManageControl = eligible to hold the controller seat (Producer/Admin).
  // hasIdentity = we know WHO this viewer is (userCap resolved an id) — without
  // it there's no way to single out one controller, so every eligible viewer
  // is treated as "in control" (the old shared-control behavior) rather than
  // locking everyone out. isController = this specific viewer currently holds
  // the seat. canControl (narrow) = actually allowed to advance cues right now.
  const canManageControl = perms.canMaster;
  const hasIdentity = !!(viewer && viewer.id);
  const isController = !hasIdentity || session.controllerId === viewer.id;
  const canControl = canManageControl && isController;
  const [people, setPeople] = useState([]);
  const [teamOpen, setTeamOpen] = useState(false);
  useEffect(() => {
    if (!canManageControl || !Api || !Api.listPeople) return;
    Api.listPeople().then((r) => setPeople((r && r.people) || [])).catch(() => {});
  }, [canManageControl]);
  const mySeat = hasIdentity ? SEATS.slice(1).find((s) => session[s.idF] === viewer.id) || null : null;
  // a viewer given the ON-GROUND / ON-AIR seat gets their side as the big panel
  useEffect(() => {
    if (mySeat) setPin(mySeat.key === "onGround" ? "onGround" : "broadcastMain");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySeat && mySeat.key]);
  const assignSeat = (seat, id) => {
    const person = people.find((p) => p.id === id) || (viewer && viewer.id === id ? viewer : null);
    pushSession({ ...session, [seat.idF]: id || null, [seat.nameF]: id ? ((person && person.name) || session[seat.nameF] || "") : "" });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const own = await dbGetBoard(date);
      if (own) { if (!cancelled) setBoard(withBoardDefaults(own)); return; }
      const wd = weekdayOf(fromKey(date));
      const def = await dbGetWeekdayDefault(wd.id);
      if (!cancelled) setBoard(def ? withBoardDefaults(def.board) : emptyBoard());
    })();
    try {
      const l = localStorage.getItem("rb:liveLayout"); if (l) setLayout(l);
      const p = localStorage.getItem("rb:livePin"); if (p) setPin(p);
      const g = localStorage.getItem("rb:liveGridCols"); if (g) setGridCols(JSON.parse(g));
    } catch (e) {}
    return () => { cancelled = true; };
  }, [date]);

  // clock, ticks every second
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const pushSession = async (next) => { setSession(next); await dbSetLive(date, next); };

  // real-time push via db.onSnapshot — replaces the old polling loop
  useEffect(() => {
    startedRef.current = false;
    const unsub = dbSubscribeLive(date, (parsed) => {
      if (parsed) { setSession(parsed); }
      else if (canManageControl && !startedRef.current) {
        // no session yet — Producer/Admin opening Live Mode IS the start action, no separate button
        // — and whoever starts it also claims the controller seat.
        startedRef.current = true;
        pushSession({ status: "live", currentIndex: 0, startedAt: Date.now(), controllerId: (viewer && viewer.id) || null, controllerName: (viewer && viewer.name) || "" });
      }
    }, () => {});
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, canManageControl]);

  useEffect(() => {
    const i = session.currentIndex || 0;
    if (i !== prevIdxRef.current) { setDir(i > prevIdxRef.current ? 1 : -1); prevIdxRef.current = i; }
  }, [session.currentIndex]);

  const setLayoutAndSave = (v) => { setLayout(v); try { localStorage.setItem("rb:liveLayout", v); } catch (e) {} };
  const setPinAndSave = (v) => { setPin(v); try { localStorage.setItem("rb:livePin", v); } catch (e) {} };
  const saveGridCols = (arr) => { setGridCols(arr); try { localStorage.setItem("rb:liveGridCols", JSON.stringify(arr)); } catch (e) {} };

  const endLive = () => pushSession({ ...session, status: "ended" });
  const takeControl = () => pushSession({ ...session, controllerId: (viewer && viewer.id) || null, controllerName: (viewer && viewer.name) || "" });
  const releaseControl = () => pushSession({ ...session, controllerId: null, controllerName: "" });
  const goTo = (i) => { if (!board || board.masterCues.length === 0) return; const idx = Math.max(0, Math.min(board.masterCues.length - 1, i)); pushSession({ ...session, currentIndex: idx }); };
  const next = () => goTo((session.currentIndex || 0) + 1);
  const prev = () => goTo((session.currentIndex || 0) - 1);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 60) { dx < 0 ? next() : prev(); }
    touchStartX.current = null;
  };

  if (!board) return <div style={{ background: "#0A0A0A", minHeight: "100vh", color: "#fff", padding: 40, textAlign: "center" }}>{t("liveModeLoading")}</div>;

  if (board.masterCues.length === 0) {
    return (
      <div style={{ background: "#0A0A0A", minHeight: "100vh", color: "#fff", padding: 40, textAlign: "center" }}>
        <button className="rb-btn" onClick={onExit} style={{ display: "flex", alignItems: "center", gap: 5, background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 6, padding: "7px 11px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", margin: "0 auto 30px" }}><ArrowLeft size={13} /> {t("liveModeExit")}</button>
        <div style={{ fontSize: 15, color: "#B9B7AE" }}>{t("today")} ({date}) {t("liveModeNoCues")}</div>
      </div>
    );
  }

  const idx = session.currentIndex || 0;
  const activeCueId = board.masterCues[idx] && board.masterCues[idx].id;
  const currentStage = (board.stages || []).find((s) => stageCueIds(s, board.masterCues).includes(activeCueId)) || null;
  // inside a bout: before its first round it's Wai Kru, from then on During
  // Fight — the one that's on now is lit, the other dimmed (no rounds: both lit)
  const fightStart = currentStage ? stageFightStart(currentStage, board.masterCues) : -1;
  const stagePhase = fightStart === -1 ? null : idx < fightStart ? "waikru" : "fight";
  const phaseStyle = (phase) => (stagePhase === null ? {} : stagePhase === phase
    ? { border: "1px solid #FF5A1F", boxShadow: "0 0 0 1px #FF5A1F inset" }
    : { opacity: 0.45 });
  const platformNames = Object.keys(board.platforms);
  const columnDefs = [
    { key: "master", label: "MASTER" },
    { key: "onGround", label: "ON-GROUND" },
    { key: "broadcastMain", label: "BROADCAST" },
    ...platformNames.map((p) => ({ key: p, label: p.toUpperCase() })),
  ];

  const activeGridKeys = (gridCols && gridCols.length ? gridCols.filter((k) => columnDefs.some((c) => c.key === k)) : columnDefs.map((c) => c.key));
  const toggleGridCol = (key) => {
    const cur = activeGridKeys;
    const has = cur.includes(key);
    if (has && cur.length <= 1) return; // keep at least one panel visible
    const next = has ? cur.filter((k) => k !== key) : [...cur, key];
    saveGridCols(next.length === columnDefs.length ? null : next);
  };

  const ownerLabel = (c) => { const n = session[seatOfColumn(c.key).nameF]; return n ? `${c.label} · ${n}` : c.label; };
  const cueFor = (colKey, cueIdx) => {
    if (colKey === "master") {
      const cue = board.masterCues[cueIdx] || null;
      const nextC = board.masterCues[cueIdx + 1] || null;
      return { title: cue?.name || "—", subtitle: null, color: cue?.color, bg: cue?.bg, icon: cue?.icon, nextTitle: nextC?.name || null };
    }
    const blocks = getBlocksFromDay(board, colKey);
    const cur = cueAtIndex(blocks, cueIdx);
    const nxt = nextCueAfter(blocks, cueIdx);
    return { title: cur?.image || (cur?.sound ? "" : t("liveModeEmpty")), subtitle: cur?.sound || null, color: cur?.color, bg: cur?.bg, nextTitle: nxt?.image || nxt?.sound || null };
  };

  // columns the producer has left open (closed ones are hidden from both the
  // grid layout AND the side/bottom "small panels" strip — one shared control)
  const visibleDefs = columnDefs.filter((c) => activeGridKeys.includes(c.key));
  const pinnedDef = visibleDefs.find((c) => c.key === pin) || visibleDefs[0] || columnDefs[0];
  const smallDefs = visibleDefs.filter((c) => c.key !== pinnedDef.key);
  const big = cueFor(pinnedDef.key, idx);
  const bigColor = colorOf(big.color || (pinnedDef.key === "master" ? "black" : "warmgray"));
  const bigBgStyle = big.bg ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.35),rgba(0,0,0,0.7)), url(${bgUrl(big.bg)})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: bigColor.fill };
  const bigTextColor = big.bg ? "#fff" : bigColor.text;

  return (
    <div style={{ background: "#0A0A0A", minHeight: "100vh", color: "#fff", fontFamily: "'Inter', -apple-system, sans-serif", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid #222", flexWrap: "wrap" }}>
        <button className="rb-btn" onClick={onExit} style={{ display: "flex", alignItems: "center", gap: 5, background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 6, padding: "7px 11px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}><ArrowLeft size={13} /> {t("liveModeExit")}</button>
        <span style={{ fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", gap: 5 }}>
          {session.status === "live" && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF3B30", animation: "rb-pulse 1.2s infinite" }} />}
          {session.status === "live" ? t("liveStatusLive") : session.status === "ended" ? t("liveStatusEnded") : t("liveStatusIdle")} · {date}
        </span>
        {canManageControl && session.status === "live" && (
          <button className="rb-btn" onClick={endLive} style={{ background: "#C1272D", border: "none", borderRadius: 6, padding: "7px 12px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{t("endLive")}</button>
        )}
        {canManageControl && hasIdentity && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#B9B7AE" }}>
            <span>{isController ? t("youAreControlling") : (session.controllerName ? `${t("controlledByPrefix")} ${session.controllerName}` : t("noOneControlling"))}</span>
            {isController ? (
              <button className="rb-btn" onClick={releaseControl} style={{ background: "#2C2C2C", border: "1px solid #3A3A3A", borderRadius: 6, padding: "5px 9px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{t("releaseControlBtn")}</button>
            ) : (
              <button className="rb-btn" onClick={takeControl} style={{ background: "#FF5A1F", border: "none", borderRadius: 6, padding: "5px 9px", color: "#161513", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{t("takeControlBtn")}</button>
            )}
          </div>
        )}
        <div style={{ display: "flex", background: "#1A1A1A", borderRadius: 6, padding: 2, gap: 2 }}>
          <button aria-label={t("layoutSideTitle")} className="rb-btn" onClick={() => setLayoutAndSave("side")} title={t("layoutSideTitle")} style={{ background: layout === "side" ? "#FF5A1F" : "transparent", border: "none", borderRadius: 4, padding: "6px 8px", color: layout === "side" ? "#161513" : "#999", cursor: "pointer" }}><PanelRight size={13} /></button>
          <button aria-label={t("layoutBottomTitle")} className="rb-btn" onClick={() => setLayoutAndSave("bottom")} title={t("layoutBottomTitle")} style={{ background: layout === "bottom" ? "#FF5A1F" : "transparent", border: "none", borderRadius: 4, padding: "6px 8px", color: layout === "bottom" ? "#161513" : "#999", cursor: "pointer" }}><PanelBottom size={13} /></button>
          <button aria-label={t("layoutGridTitle")} className="rb-btn" onClick={() => setLayoutAndSave("grid")} title={t("layoutGridTitle")} style={{ background: layout === "grid" ? "#FF5A1F" : "transparent", border: "none", borderRadius: 4, padding: "6px 8px", color: layout === "grid" ? "#161513" : "#999", cursor: "pointer" }}><Split size={13} /></button>
        </div>
        {layout !== "grid" && (
          <select aria-label={t("focusPrefix")} value={pinnedDef.key} onChange={(e) => setPinAndSave(e.target.value)} style={{ background: "#1A1A1A", color: "#fff", border: "1px solid #2C2C2C", borderRadius: 6, padding: "6px 8px", fontSize: 11.5 }}>
            {visibleDefs.map((c) => <option key={c.key} value={c.key}>{t("focusPrefix")} {c.label}</option>)}
          </select>
        )}
        <div style={{ position: "relative" }}>
          <button className="rb-btn" onClick={() => setColMenuOpen((v) => !v)} title={t("columnsToggleHint")}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "#1A1A1A", color: "#fff", border: "1px solid #2C2C2C", borderRadius: 6, padding: "6px 8px", fontSize: 11.5, cursor: "pointer" }}>
            {t("columnsBtn")} ({activeGridKeys.length}/{columnDefs.length}) <ChevronDown size={12} />
          </button>
          {colMenuOpen && (
            <>
              <div onClick={() => setColMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 8, padding: 5, zIndex: 50, minWidth: 180, boxShadow: "0 10px 28px rgba(0,0,0,0.45)" }}>
                {columnDefs.map((c) => {
                  const on = activeGridKeys.includes(c.key);
                  return (
                    <label key={c.key} onClick={(e) => e.stopPropagation()}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 5, cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: on ? "#fff" : "#7A786F" }}>
                      <input type="checkbox" checked={on} onChange={() => toggleGridCol(c.key)} style={{ accentColor: "#FF5A1F", cursor: "pointer" }} />
                      {c.label}
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div style={{ background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 6, padding: "6px 12px", fontSize: 13, fontWeight: 800, fontFamily: "monospace" }}>
          {now.toLocaleTimeString("th-TH", { hour12: false })}
        </div>
      </div>

      {(canManageControl || SEATS.some((s) => session[s.nameF])) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "8px 14px", borderBottom: "1px solid #222", flexWrap: "wrap", fontSize: 11.5, color: "#B9B7AE" }}>
          {mySeat && <span style={{ background: "#FF5A1F", color: "#161513", borderRadius: 6, padding: "3px 8px", fontWeight: 800 }}>{t("youRunSeat")} {t(mySeat.labelKey)}</span>}
          {teamOpen && canManageControl ? SEATS.map((s) => (
            <label key={s.key} style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 700 }}>
              {t(s.labelKey)}
              <select aria-label={t(s.labelKey)} value={session[s.idF] || ""} onChange={(e) => assignSeat(s, e.target.value)}
                style={{ background: "#1A1A1A", color: "#fff", border: "1px solid #2C2C2C", borderRadius: 6, padding: "5px 8px", fontSize: 11.5 }}>
                <option value="">{t("seatNobody")}</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                {session[s.idF] && !people.some((p) => p.id === session[s.idF]) && <option value={session[s.idF]}>{session[s.nameF] || session[s.idF]}</option>}
              </select>
            </label>
          )) : SEATS.map((s) => (
            <span key={s.key}>{t(s.labelKey)}: <b style={{ color: "#fff" }}>{session[s.nameF] || "—"}</b></span>
          ))}
          {canManageControl && (
            <button className="rb-btn" onClick={() => setTeamOpen((v) => !v)} style={{ background: teamOpen ? "#FF5A1F" : "#2C2C2C", border: "1px solid #3A3A3A", borderRadius: 6, padding: "5px 9px", color: teamOpen ? "#161513" : "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              {teamOpen ? t("teamDone") : t("teamEdit")}
            </button>
          )}
        </div>
      )}

      {currentStage && (currentStage.waikruText || currentStage.waikruBg || currentStage.duringFightText || currentStage.duringFightBg) && (
        <div style={{ display: "flex", gap: 8, padding: "8px 10px 0", flexWrap: "wrap", justifyContent: "center" }}>
          {(currentStage.waikruText || currentStage.waikruBg) && (
            <div data-stage-phase="waikru" data-now={stagePhase === "waikru" ? "1" : undefined} style={{ flex: "1 1 220px", maxWidth: 380, display: "flex", alignItems: "center", gap: 10, background: "#151515", border: "1px solid #262626", borderRadius: 8, padding: 10, ...phaseStyle("waikru") }}>
              {currentStage.waikruBg && (
                <div style={{ width: 52, height: 52, borderRadius: 6, flex: "0 0 auto", backgroundImage: `url(${bgUrl(currentStage.waikruBg)})`, backgroundSize: "cover", backgroundPosition: "center" }} />
              )}
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.6 }}>{currentStage.stage ? `${currentStage.stage} · ` : ""}{t("waikruLabel")}{stagePhase === "waikru" ? ` · ${t("stageNow")}` : ""}</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: "pre-line" }}>{currentStage.waikruText}</div>
              </div>
            </div>
          )}
          {(currentStage.duringFightText || currentStage.duringFightBg) && (
            <div data-stage-phase="fight" data-now={stagePhase === "fight" ? "1" : undefined} style={{ flex: "1 1 220px", maxWidth: 380, display: "flex", alignItems: "center", gap: 10, background: "#151515", border: "1px solid #262626", borderRadius: 8, padding: 10, ...phaseStyle("fight") }}>
              {currentStage.duringFightBg && (
                <div style={{ width: 52, height: 52, borderRadius: 6, flex: "0 0 auto", backgroundImage: `url(${bgUrl(currentStage.duringFightBg)})`, backgroundSize: "cover", backgroundPosition: "center" }} />
              )}
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.6 }}>{currentStage.stage ? `${currentStage.stage} · ` : ""}{t("duringFightLabel")}{stagePhase === "fight" ? ` · ${t("stageNow")}` : ""}</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: "pre-line" }}>{currentStage.duringFightText}</div>
                {currentStage.sound && <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>🔊 {currentStage.sound}</div>}
              </div>
            </div>
          )}
        </div>
      )}

      {layout === "grid" ? (
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${activeGridKeys.length <= 2 ? 320 : activeGridKeys.length <= 4 ? 260 : 200}px, 1fr))`, gridAutoRows: "1fr", gap: 8, padding: 10 }}
          onTouchStart={canControl ? onTouchStart : undefined} onTouchEnd={canControl ? onTouchEnd : undefined}>
          {activeGridKeys.map((key) => {
            const def = columnDefs.find((c) => c.key === key);
            const cue = cueFor(key, idx);
            const gColor = colorOf(cue.color || (key === "master" ? "black" : "warmgray"));
            const gBg = cue.bg ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.35),rgba(0,0,0,0.7)), url(${bgUrl(cue.bg)})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: gColor.fill };
            const gText = cue.bg ? "#fff" : gColor.text;
            return (
              <div key={key} onClick={canControl ? next : undefined}
                style={{ ...gBg, borderRadius: 8, position: "relative", padding: 16, minHeight: 140, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: canControl ? "pointer" : "default", border: key === "master" ? "2px solid rgba(255,255,255,0.18)" : "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.65, color: gText, position: "absolute", top: 12, left: 14 }}>{ownerLabel(def)}</div>
                <div key={idx} style={{ display: "flex", flexDirection: "column", alignItems: "center", animation: `${dir > 0 ? "rb-slide-in-right" : "rb-slide-in-left"} 0.32s ease-out`, textAlign: "center" }}>
                  {cue.icon && <Icon id={cue.icon} size={26} color={gText} />}
                  <div style={{ fontSize: "clamp(15px, 2.2vw, 26px)", fontWeight: 900, color: gText, marginTop: 6, lineHeight: 1.25, whiteSpace: "pre-line" }}>{cue.title}</div>
                  {cue.subtitle && <div style={{ fontSize: 12, color: gText, opacity: 0.85, marginTop: 4, whiteSpace: "pre-line" }}>🔊 {cue.subtitle}</div>}
                </div>
                {cue.nextTitle && (
                  <div style={{ position: "absolute", bottom: 10, left: 14, right: 14, fontSize: 11, color: gText, opacity: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {t("nextArrow")} {cue.nextTitle}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
      <div style={{ flex: 1, display: "flex", flexDirection: layout === "bottom" ? "column" : "row", gap: 8, padding: 10 }}
        onTouchStart={canControl ? onTouchStart : undefined} onTouchEnd={canControl ? onTouchEnd : undefined}>
        <div onClick={canControl ? next : undefined}
          style={{ flex: layout === "bottom" ? "3 1 0" : "3 1 0", ...bigBgStyle, borderRadius: 6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", padding: 24, cursor: canControl ? "pointer" : "default", minHeight: layout === "bottom" ? 220 : 320 }}>
          <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.65, color: bigTextColor, position: "absolute", top: 14, left: 18 }}>{ownerLabel(pinnedDef)}</div>
          <div key={idx} style={{ display: "flex", flexDirection: "column", alignItems: "center", animation: `${dir > 0 ? "rb-slide-in-right" : "rb-slide-in-left"} 0.32s ease-out` }}>
            {big.icon && <Icon id={big.icon} size={40} color={bigTextColor} />}
            <div style={{ fontSize: "clamp(22px, 4.5vw, 52px)", fontWeight: 900, color: bigTextColor, textAlign: "center", marginTop: 10, lineHeight: 1.25, whiteSpace: "pre-line" }}>{big.title}</div>
            {big.subtitle && <div style={{ fontSize: 15, color: bigTextColor, opacity: 0.85, marginTop: 6, whiteSpace: "pre-line" }}>🔊 {big.subtitle}</div>}
          </div>
          {big.nextTitle && (
            <div style={{ position: "absolute", bottom: 14, left: 18, fontSize: 12, color: bigTextColor, opacity: 0.75, textAlign: "left" }}>
              {t("nextArrow")} {big.nextTitle}
            </div>
          )}
          {canControl && (
            <div style={{ position: "absolute", bottom: 14, right: 18, display: "flex", gap: 6 }}>
              <button aria-label={t("a11yPrevCue")} className="rb-btn" onClick={(e) => { e.stopPropagation(); prev(); }} style={{ background: "rgba(0,0,0,0.35)", border: "none", borderRadius: 6, color: "#fff", padding: 8, cursor: "pointer" }}><ChevronLeft size={16} /></button>
              <button aria-label={t("a11yNextCue")} className="rb-btn" onClick={(e) => { e.stopPropagation(); next(); }} style={{ background: "rgba(0,0,0,0.35)", border: "none", borderRadius: 6, color: "#fff", padding: 8, cursor: "pointer" }}><ChevronRight size={16} /></button>
            </div>
          )}
        </div>

        <div style={{ flex: "1 1 0", display: "flex", flexDirection: layout === "bottom" ? "row" : "column", gap: 8, overflow: "auto" }}>
          <div style={{ flex: "0 0 auto", background: "#151515", border: "1px solid #262626", borderRadius: 6, padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.6 }}>MASTER CLOCK</div>
            <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "monospace", color: "#FF5A1F" }}>{now.toLocaleTimeString("th-TH", { hour12: false })}</div>
          </div>
          {smallDefs.map((c) => {
            const cue = cueFor(c.key, idx);
            const cColor = colorOf(cue.color || "warmgray");
            const cBg = cue.bg ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.35),rgba(0,0,0,0.7)), url(${bgUrl(cue.bg)})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: cColor.fill };
            const cText = cue.bg ? "#fff" : cColor.text;
            return (
              <div key={c.key} style={{ flex: "1 1 0", minHeight: 90, ...cBg, borderRadius: 6, padding: 10, position: "relative", color: cText, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.65 }}>{ownerLabel(c)}</div>
                <div style={{ fontSize: 14, fontWeight: 800, marginTop: 4, whiteSpace: "pre-line" }}>{cue.title}</div>
                {cue.subtitle && <div style={{ fontSize: 11, opacity: 0.85, whiteSpace: "pre-line" }}>🔊 {cue.subtitle}</div>}
                {cue.nextTitle && <div style={{ position: "absolute", bottom: 6, left: 10, fontSize: 11, opacity: 0.7 }}>{t("nextArrow")} {cue.nextTitle}</div>}
              </div>
            );
          })}
        </div>
      </div>
      )}
      {!canManageControl && <div style={{ textAlign: "center", fontSize: 11, color: "#7A786F", padding: "6px 0 10px" }}>{t("viewOnlyNote")}</div>}
      {canManageControl && hasIdentity && !isController && <div style={{ textAlign: "center", fontSize: 11, color: "#7A786F", padding: "6px 0 10px" }}>{t("notControllingNote")}</div>}
    </div>
  );
}

export { cueAtIndex, nextCueAfter, LiveModeScreen };
